import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowRight, LockKeyhole, MessageCircleMore, ShieldCheck } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { useToast } from '../../components/ui/toast'
import { api, setCsrfToken } from '../../lib/api'

export function LoginPage() {
  const [username, setUsername] = useState('admin')
  const [password, setPassword] = useState('')
  const queryClient = useQueryClient()
  const { showToast } = useToast()
  const login = useMutation({
    mutationFn: () => api.login(username, password),
    onSuccess: (session) => {
      setCsrfToken(session.csrfToken)
      queryClient.setQueryData(['session'], session)
    },
    onError: (error) =>
      showToast(error instanceof Error ? error.message : 'Accesso non riuscito', 'error'),
  })

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!username.trim() || !password) return
    login.mutate()
  }

  return (
    <main className="grid min-h-svh lg:grid-cols-[1.05fr_0.95fr]">
      <section className="relative hidden overflow-hidden bg-[#123d39] p-12 text-[#f4fbf8] lg:flex lg:flex-col lg:justify-between">
        <div className="flex items-center gap-3">
          <div className="grid size-11 place-items-center rounded-2xl bg-[#f4fbf8] font-black text-[#0f766e]">
            P2
          </div>
          <span className="text-lg font-bold tracking-tight">PEMMY2</span>
        </div>
        <div className="max-w-xl">
          <div className="mb-8 inline-flex items-center gap-2 rounded-full border border-[#c7eee5]/25 bg-[#f4fbf8]/10 px-3 py-1.5 text-sm text-[#d8f5ee]">
            <ShieldCheck className="size-4" /> Inbox operativa protetta
          </div>
          <h1 className="text-balance text-5xl font-semibold leading-[1.08] tracking-tight">
            Le conversazioni WhatsApp, finalmente ordinate.
          </h1>
          <p className="mt-6 max-w-lg text-lg leading-8 text-[#c7ded9]">
            Una superficie veloce e affidabile per seguire i clienti, costruita su Dualhook e
            storico SQLite.
          </p>
        </div>
        <div className="flex items-center gap-3 text-sm text-[#aacbc4]">
          <MessageCircleMore className="size-5" /> PEMMY2 Core · v0.1
        </div>
      </section>

      <section className="flex min-h-svh items-center justify-center px-5 py-10 sm:px-10">
        <div className="w-full max-w-[420px]">
          <div className="mb-10 flex items-center gap-3 lg:hidden">
            <div className="grid size-11 place-items-center rounded-2xl bg-[var(--primary)] font-black text-[var(--primary-foreground)]">
              P2
            </div>
            <span className="text-lg font-bold tracking-tight">PEMMY2</span>
          </div>
          <div className="mb-8">
            <div className="mb-5 grid size-12 place-items-center rounded-2xl bg-[var(--primary-soft)] text-[var(--primary)]">
              <LockKeyhole className="size-5" />
            </div>
            <h2 className="text-3xl font-semibold tracking-tight">Bentornato</h2>
            <p className="mt-2 text-base leading-6 text-[var(--muted-foreground)]">
              Accedi per gestire messaggi e configurazione Dualhook.
            </p>
          </div>
          <form onSubmit={submit} className="flex flex-col gap-5">
            <label className="flex flex-col gap-2 text-sm font-semibold">
              Nome utente
              <Input
                autoComplete="username"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
              />
            </label>
            <label className="flex flex-col gap-2 text-sm font-semibold">
              Password
              <Input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </label>
            <Button type="submit" className="mt-2 w-full" disabled={login.isPending}>
              {login.isPending ? 'Accesso in corso…' : 'Accedi'}
              {!login.isPending && <ArrowRight className="size-4" />}
            </Button>
          </form>
          <p className="mt-8 text-center text-sm leading-5 text-[var(--muted-foreground)]">
            La sessione è protetta e scade automaticamente dopo il periodo configurato.
          </p>
        </div>
      </section>
    </main>
  )
}
